/**
 * Spec 79 M1.5 — Orchestrator turn state + event helpers for useConversationEvents.
 *
 * Handles synthetic message.appended envelopes emitted by turn-streamer.ts.
 * Synthetic events have ids like "orchestrator:${eventType}:${messageId}" and
 * are transient — they drive the streaming UX but are NOT added to state.messages.
 *
 * Functions here operate on OrchestratorTurnState (the slice) rather than
 * the full ConversationEventsState to avoid circular imports.
 */
import { OrchestratorEventSchema } from '@confluent/shared/lib/orchestrator/events';
import { logger } from '@confluent/shared/lib/logger';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrchestratorToolCallState {
  toolName: string;
  inputPreview: string;
  status: 'pending' | 'completed' | 'failed';
  durationMs?: number;
  toolMessageId?: number;
  panelLink?: string | null;
  error?: { errorClass: string; message: string };
}

export interface OrchestratorTurnState {
  /** Lifecycle status of the current orchestrator turn. */
  status: 'idle' | 'thinking' | 'tool_calling' | 'streaming_text' | 'completed' | 'failed';
  /** Active tool calls indexed by toolUseId. Preserved across turn_completed until next turn_started. */
  activeToolCalls: Map<string, OrchestratorToolCallState>;
  /** Accumulated assistant text during the turn. Cleared on turn_completed. */
  streamingText: string;
  /** Most recent error from turn_failed. Cleared on next turn_started. */
  lastError: string | null;
}

// ─── Defensive caps ───────────────────────────────────────────────────────────
//
// The reducer is driven by server SSE events. A buggy or prompt-injected agent
// could emit unbounded text or an unbounded number of tool calls within one
// turn — capping here prevents browser-tab OOM / freeze. These ceilings are
// generous: any realistic turn fits well below them.
const MAX_STREAMING_TEXT_CHARS = 50_000;
const MAX_ACTIVE_TOOL_CALLS = 50;
const LOG_RAW_CONTENT_CHARS = 200;

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function makeInitialOrchestratorTurn(): OrchestratorTurnState {
  return {
    status: 'idle',
    activeToolCalls: new Map(),
    streamingText: '',
    lastError: null,
  };
}

/**
 * Applies a single OrchestratorEvent (parsed from a synthetic message.appended)
 * to the OrchestratorTurnState slice. Returns `turn` unchanged on error.
 */
export function applyOrchestratorEventToTurn(turn: OrchestratorTurnState, rawContent: string): OrchestratorTurnState {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawContent);
  } catch {
    logger.warn('[useConversationEvents] Failed to parse orchestrator event JSON', {
      rawContent: rawContent.slice(0, LOG_RAW_CONTENT_CHARS),
    });
    return turn;
  }

  const result = OrchestratorEventSchema.safeParse(parsed);
  if (!result.success) {
    logger.warn('[useConversationEvents] Orchestrator event failed Zod validation', {
      errors: result.error.issues,
    });
    return turn;
  }

  const event = result.data;

  switch (event.type) {
    case 'orchestrator.turn_started':
      return { status: 'thinking', activeToolCalls: new Map(), streamingText: '', lastError: null };

    case 'orchestrator.thinking_started':
    case 'orchestrator.thinking_chunk':
      // thinking_chunk carries a redacted summary that we don't surface in M1.5;
      // both events just keep the indicator in the 'thinking' state.
      return turn.status === 'thinking' ? turn : { ...turn, status: 'thinking' };

    case 'orchestrator.text_chunk': {
      if (turn.streamingText.length >= MAX_STREAMING_TEXT_CHARS) return turn;
      const remaining = MAX_STREAMING_TEXT_CHARS - turn.streamingText.length;
      const next = turn.streamingText + event.text.slice(0, remaining);
      return { ...turn, status: 'streaming_text', streamingText: next };
    }

    case 'orchestrator.tool_call_started': {
      if (turn.activeToolCalls.size >= MAX_ACTIVE_TOOL_CALLS) {
        logger.warn('[useConversationEvents] activeToolCalls cap reached, dropping event', {
          toolUseId: event.toolUseId,
        });
        return turn;
      }
      const newMap = new Map(turn.activeToolCalls);
      newMap.set(event.toolUseId, {
        toolName: event.toolName,
        inputPreview: event.inputPreview,
        status: 'pending',
      });
      return { ...turn, status: 'tool_calling', activeToolCalls: newMap };
    }

    case 'orchestrator.tool_call_completed': {
      const newMap = new Map(turn.activeToolCalls);
      const existing = newMap.get(event.toolUseId);
      newMap.set(event.toolUseId, {
        ...(existing ?? { toolName: event.toolName, inputPreview: '' }),
        status: 'completed',
        durationMs: event.durationMs,
        toolMessageId: event.toolMessageId,
        panelLink: event.panelLink ?? null,
      });
      return { ...turn, activeToolCalls: newMap };
    }

    case 'orchestrator.tool_call_failed': {
      const newMap = new Map(turn.activeToolCalls);
      const existing = newMap.get(event.toolUseId);
      newMap.set(event.toolUseId, {
        ...(existing ?? { toolName: event.toolName, inputPreview: '' }),
        status: 'failed',
        error: { errorClass: event.error.errorClass, message: event.error.message },
      });
      return { ...turn, activeToolCalls: newMap };
    }

    case 'orchestrator.turn_completed':
      return { ...turn, status: 'completed', streamingText: '' };

    case 'orchestrator.turn_failed':
      return { ...turn, status: 'failed', streamingText: '', lastError: event.detail };
  }
}
